// TimelineForm.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Skeleton,
  Fade,
  Grow,
  Chip,
  Button,
  IconButton,
  Collapse,
  useTheme,
  alpha,
  Tooltip,
  Badge,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditNoteIcon from "@mui/icons-material/EditNote";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import PersonIcon from "@mui/icons-material/Person";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EventIcon from "@mui/icons-material/Event";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import HandshakeIcon from "@mui/icons-material/Handshake";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FolderIcon from "@mui/icons-material/Folder";
import CommentIcon from "@mui/icons-material/Comment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import RefreshIcon from "@mui/icons-material/Refresh";
import TimelineIcon from "@mui/icons-material/Timeline";
import axiosInstance from "../../../../../../services/axiosInstance";
import apiEndPointsConfig from "../../../../../../services/apiEndPoints";

const TimelineForm = ({ dealId }) => {
  const theme = useTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [groupedView, setGroupedView] = useState(false);

  // Pagination states (Load More)
  const PAGE_SIZE = 3;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const DESCRIPTION_CHAR_LIMIT = 100;

  const formatTime = (dateString) => {
    if (!dateString) return { time: "", date: "", relative: "" };

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    let relative = "";
    if (diffMins < 1) relative = "Just now";
    else if (diffMins < 60) relative = `${diffMins}m ago`;
    else if (diffHours < 24) relative = `${diffHours}h ago`;
    else if (diffDays < 7) relative = `${diffDays}d ago`;
    else if (diffDays < 30) relative = `${Math.floor(diffDays / 7)}w ago`;
    else
      relative = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });

    const time = date.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const dateStr = date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });

    return { time, date: dateStr, relative };
  };

  const getIconMeta = (title = "", description = "") => {
    const content = `${title}`.toLowerCase();

    // ✅ MUST be first — "Assignee Changed" contains 'changed' which would match generic edit check
    if (content.match(/assignee changed|reassigned/)) {
      return {
        icon: <SupervisedUserCircleIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #e53935 0%, #ff6b6b 100%)",
        color: "#e53935",
        category: "assignee",
      };
    }

    if (content.match(/file|upload|document|attachment|pdf|excel|csv/)) {
      return {
        icon: <CloudUploadIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#667eea",
        category: "document",
      };
    }

    if (content.match(/update|edit|change|modif/)) {
      return {
        icon: <EditNoteIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
        color: "#f093fb",
        category: "edit",
      };
    }

    if (content.match(/note|comment|remark|observation/)) {
      return {
        icon: <CommentIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
        color: "#4facfe",
        category: "note",
      };
    }

    if (content.match(/email|mail|message/)) {
      return {
        icon: <EmailIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
        color: "#43e97b",
        category: "communication",
      };
    }

    if (content.match(/call|phone|contact|dial/)) {
      return {
        icon: <PhoneIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
        color: "#30cfd0",
        category: "communication",
      };
    }

    if (content.match(/won|success|closed|complete|finish|achieve/)) {
      return {
        icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)",
        color: "#56ab2f",
        category: "success",
      };
    }

    if (content.match(/lost|fail|reject|cancel|decline/)) {
      return {
        icon: <WarningIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)",
        color: "#ff6b6b",
        category: "warning",
      };
    }

    if (content.match(/amount|price|value|payment|invoice|money|cost|budget/)) {
      return {
        icon: <AttachMoneyIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)",
        color: "#f2994a",
        category: "financial",
      };
    }

    if (content.match(/meeting|event|schedule|appointment|calendar/)) {
      return {
        icon: <EventIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
        color: "#a8edea",
        category: "event",
      };
    }

    if (content.match(/contact|person|user|client|customer|lead/)) {
      return {
        icon: <PersonIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
        color: "#ffecd2",
        category: "person",
      };
    }

    if (content.match(/deal|opportunity|proposal|negotiation/)) {
      return {
        icon: <HandshakeIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)",
        color: "#a1c4fd",
        category: "deal",
      };
    }

    if (content.match(/progress|status|stage|move|advance/)) {
      return {
        icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #13547a 0%, #80d0c7 100%)",
        color: "#13547a",
        category: "progress",
      };
    }
    if (title?.toLowerCase().trim() === "Note added") {
      return {
        icon: <NoteAltIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
        color: "#4facfe",
        category: "note",
      };
    }

    if (content.match(/task|assign|todo|action/)) {
      return {
        icon: <AssignmentIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #f77062 0%, #fe5196 100%)",
        color: "#f77062",
        category: "task",
      };
    }


    if (content.match(/delete|remove|trash|discard/)) {
      return {
        icon: <DeleteIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #ee9ca7 0%, #ffdde1 100%)",
        color: "#ee9ca7",
        category: "delete",
      };
    }

    if (content.match(/add|create|new|insert/)) {
      return {
        icon: <AddCircleIcon sx={{ fontSize: 20 }} />,
        gradient: "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
        color: "#84fab0",
        category: "create",
      };
    }

    return {
      icon: <FolderIcon sx={{ fontSize: 20 }} />,
      gradient: "linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)",
      color: "#e0c3fc",
      category: "general",
    };
  };

  const truncateDescription = (text, limit = DESCRIPTION_CHAR_LIMIT) => {
    if (!text || text.length <= limit) return { text, isTruncated: false };

    const truncated = text.substring(0, limit);
    const lastSpace = truncated.lastIndexOf(" ");
    const finalText =
      lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated;

    return {
      text: finalText + "...",
      fullText: text,
      isTruncated: true,
    };
  };

  const fetchTimeline = async () => {
    if (!dealId) return;

    try {
      setLoading(true);
      setError("");

      const url = apiEndPointsConfig.getAllTimeline.replace(":dealId", dealId);
      const res = await axiosInstance.get(url);
      const timelines = res.data.data || res.data || [];

      const mappedEvents = timelines.map((item, index) => {
        // ---- SPECIAL HANDLING FOR CALL LOG EVENTS ----
        let title = item.title;
        let description = item.description;
        let userName = item.userName;

        if (item.type === "call-log") {
          // Build a meaningful title & description for call logs
          title =
            title ||
            `Call #${item.callId || "-"}${item.status ? ` - ${item.status}` : ""
            }`;

          const parts = [];
          if (item.mobile) parts.push(`Mobile: ${item.mobile}`);
          if (item.callDate || item.callTime)
            parts.push(
              `Call at: ${item.callDate || ""} ${item.callTime ? `· ${item.callTime}` : ""
              }`
            );
          if (item.agent) parts.push(`Agent: ${item.agent}`);
          if (item.direction) parts.push(`Direction: ${item.direction}`);
          if (item.sub_disposition) parts.push(`Disposition: ${item.sub_disposition}`);
          if (item.timeDuration) parts.push(`Duration: ${item.timeDuration}s`);

          description = description || parts.join(" | ");
          // For UI "by" name we can show agent
          userName = item.agent || item.userName;
        }

        const iconMeta = getIconMeta(title, description);
        const timeInfo = formatTime(item.changeTime);
        const descriptionInfo = truncateDescription(description);

        return {
          id: item._id,
          type: item.type || "timeline",
          callId: item.callId,
          callDate: item.callDate,
          callTime: item.callTime,
          status: item.status,
          mobile: item.mobile,
          agent: item.agent,
          location: item.location,
          blobUrl: item.blobUrl,
          ...timeInfo,
          ...iconMeta,
          title,
          description,
          descriptionInfo,
          isFirst: index === 0,
          isLast: index === timelines.length - 1,
          changeTime: item.changeTime,
          userName,
        };
      });

      setEvents(mappedEvents);
      setVisibleCount(PAGE_SIZE); // reset pagination
    } catch (err) {
      console.error("Error fetching deal timeline:", err);
      setError("Failed to load timeline. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeline();
  }, [dealId]);

  const groupedEvents = useMemo(() => {
    if (!groupedView) return null;

    const groups = {};
    events.forEach((event) => {
      const dateKey = event.date;
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(event);
    });
    return groups;
  }, [events, groupedView]);

  const TimelineSkeleton = () => (
    <Box sx={{ width: "100%" }}>
      {[1, 2, 3, 4].map((item) => (
        <Box
          key={item}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mb: 4,
            opacity: 1 - item * 0.15,
          }}
        >
          <Box sx={{ width: { xs: 80, sm: 110 }, pr: 2, mr: 1 }}>
            <Skeleton variant="text" width={70} height={20} />
            <Skeleton variant="text" width={50} height={16} />
          </Box>
          <Box sx={{ mr: 2 }}>
            <Skeleton variant="circular" width={16} height={16} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="40%" height={24} />
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="60%" height={20} />
              </Box>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  const EmptyState = () => (
    <Fade in timeout={500}>
      <Paper
        elevation={0}
        sx={{
          p: 5,
          textAlign: "center",
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          borderRadius: 3,
          border: `2px dashed ${alpha(theme.palette.primary.main, 0.15)}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 600,
            mb: 1,
          }}
        >
          No Timeline Events Yet
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: 300,
            margin: "0 auto",
          }}
        >
          Timeline events will appear here as the deal progresses through
          different stages
        </Typography>
      </Paper>
    </Fade>
  );

  const ErrorState = () => (
    <Fade in timeout={500}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          bgcolor: alpha(theme.palette.error.main, 0.05),
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: alpha(theme.palette.error.main, 0.1),
            }}
          >
            <WarningIcon sx={{ color: theme.palette.error.main }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body1"
              sx={{ color: theme.palette.error.main, fontWeight: 600 }}
            >
              Unable to Load Timeline
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
            >
              {error}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={fetchTimeline}
            startIcon={<RefreshIcon />}
            sx={{
              borderRadius: 20,
              textTransform: "none",
            }}
          >
            Retry
          </Button>
        </Box>
      </Paper>
    </Fade>
  );

  if (loading) return <TimelineSkeleton />;
  if (error) return <ErrorState />;
  if (!loading && !error && events.length === 0) return <EmptyState />;

  const TimelineEvent = ({ event, index }) => {
    const isExpanded = expandedItems.has(event.id);
    const { descriptionInfo } = event;

    return (
      <Grow in timeout={400 + index * 100} style={{ transformOrigin: "0 0 0" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mb: event.isLast ? 0 : 2,
            position: "relative",
            "&:hover .timeline-content": {
              transform: "translateX(4px)",
              "& .timeline-card": {
                boxShadow: `0 8px 24px ${alpha(event.color, 0.15)}`,
              },
            },
          }}
        >
          <Box
            sx={{
              width: { xs: 80, sm: 110 },
              textAlign: "right",
              pr: 3,
              pt: 0.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontSize: { xs: 12, sm: 13 },
                fontWeight: 600,
              }}
            >
              {event.time}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.primary.main,
                fontSize: { xs: 10, sm: 11 },
                fontWeight: 500,
              }}
            >
              {event.relative}
            </Typography>
            {!groupedView && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: theme.palette.text.disabled,
                  fontSize: { xs: 9, sm: 10 },
                  mt: 0.5,
                }}
              >
                {event.date}
              </Typography>
            )}
          </Box>

          <Box
            className="timeline-content"
            sx={{
              flexGrow: 1,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Paper
              className="timeline-card"
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(event.color, 0.15)}`,
                bgcolor: alpha(event.color, 0.02),
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 4,
                  height: "100%",
                  background: event.gradient,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  badgeContent={
                    event.category && (
                      <Box
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: 9,
                          fontWeight: 600,
                          color: event.color,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      />
                    )
                  }
                >
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      background: event.gradient,
                      boxShadow: `0 4px 14px 0 ${alpha(event.color, 0.3)}`,
                      "& svg": {
                        color: "white",
                      },
                    }}
                  >
                    {event.icon}
                  </Avatar>
                </Badge>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: { xs: 14, sm: 15 },
                        color: theme.palette.text.primary,
                        fontWeight: 700,
                      }}
                    >
                      {event.title}
                    </Typography>
                    {event.isFirst && (
                      <Chip
                        label="Latest"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 600,
                          bgcolor: theme.palette.success.main,
                          color: "white",
                        }}
                      />
                    )}
                  </Box>

                  {event.userName && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <PersonIcon
                        sx={{
                          fontSize: 14,
                          color: theme.palette.text.disabled,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: 11,
                        }}
                      >
                        {event.userName}
                      </Typography>
                    </Box>
                  )}

                  {/* EXTRA INFO ROW FOR CALL LOGS: CALL ID + CALL DATE/TIME */}
                  {event.type === "call-log" && (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      {event.callId && (
                        <Chip
                          size="small"
                          label={`Call ID: ${event.callId}`}
                          sx={{
                            height: 20,
                            fontSize: 11,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                          }}
                        />
                      )}
                      {(event.callDate || event.callTime) && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: 11,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <AccessTimeIcon sx={{ fontSize: 13 }} />
                          {event.callDate || "-"}
                          {event.callTime ? ` · ${event.callTime}` : ""}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: { xs: 12, sm: 13 },
                      lineHeight: 1.6,
                      wordBreak: "break-word",
                      textAlign: "start",
                      width: "100%",
                    }}
                  >
                    {event.category === "assignee" ? (
                      (() => {
                        const text = isExpanded || !descriptionInfo.isTruncated
                          ? event.description
                          : descriptionInfo.text;
                        // Bold the quoted names: e.g. "John" → <strong>John</strong>
                        const parts = text ? text.split(/("[^"]+")/) : [text];
                        return parts.map((part, i) =>
                          part.startsWith('"') && part.endsWith('"') ? (
                            <strong key={i} style={{ color: "#1a1a1a", fontWeight: 700 }}>
                              {part.slice(1, -1)}
                            </strong>
                          ) : (
                            <span key={i}>{part}</span>
                          )
                        );
                      })()
                    ) : (
                      isExpanded || !descriptionInfo.isTruncated
                        ? event.description
                        : descriptionInfo.text
                    )}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Grow>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        px: { xs: 0, sm: 1 },
        py: 1,
      }}
    >
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            Activity Timeline
          </Typography>
          {events.length > 0 && (
            <Chip
              label={`${events.length} events`}
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            />
          )}
        </Box>
      </Box>

      {/* Render timeline with pagination */}
      <Box>
        {events.slice(0, visibleCount).map((event, index) => (
          <TimelineEvent key={event.id} event={event} index={index} />
        ))}

        {visibleCount < events.length && (
          <Box sx={{ textAlign: "start", mt: 0 }}>
            <Chip
              onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
              label="View more"
              size="small"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TimelineForm;
